import { setAttributes } from '~/lib/utils/dom_utils';

export const iframeRenderingEnabled = () =>
  Boolean(window.gon?.iframe_rendering_providers && window.gon?.features?.allowIframesInMarkdown);

const originOf = (src) => {
  try {
    return new URL(src, window.location.origin).origin;
  } catch {
    return null;
  }
};

export const iframeProviderFor = (src, providerId) => {
  if (!iframeRenderingEnabled()) return null;

  const providers = window.gon.iframe_rendering_providers;
  if (!Object.prototype.hasOwnProperty.call(providers, providerId)) return null;

  const provider = providers[providerId];

  return provider.src_origin === originOf(src) ? provider : null;
};

export const isIframeSrcAllowed = (src, providerId) => Boolean(iframeProviderFor(src, providerId));

const elsProcessingMap = new WeakMap();

function renderIframeEl(el) {
  const { src } = el;

  const provider = iframeProviderFor(src, el.dataset.iframeProviderId);
  if (!provider) {
    // This URL passed the allowlist at the time the Markdown content was
    // created/last updated, but no longer does. We must remove the node
    // entirely: if this instance uses the asset proxy, allowing it to remain in
    // the DOM would create a bypass. Re-modifying the source content will allow
    // it to show again (through the asset proxy, if enabled).
    el.remove();
    return;
  }

  const width = el.getAttribute('width');
  const height = el.getAttribute('height');
  const hasExplicitDimensions = width || height;

  const iframeEl = document.createElement('iframe');
  setAttributes(iframeEl, {
    src,
    class: 'gl-border-none',
    sandbox: provider.sandbox,
    allowfullscreen: 'true',
    referrerpolicy: 'strict-origin-when-cross-origin',
  });
  iframeEl.style.maxHeight = '80vh';

  if (hasExplicitDimensions) {
    if (width) iframeEl.setAttribute('width', width);
    if (height) iframeEl.setAttribute('height', height);
    iframeEl.style.maxWidth = '100%';
    if (width && height) {
      iframeEl.style.aspectRatio = `${width} / ${height}`;
      iframeEl.style.height = 'auto';
    }
  } else {
    iframeEl.classList.add('gl-inset-0', 'gl-h-full', 'gl-w-full');
  }

  const wrapper = document.createElement('div');
  wrapper.appendChild(iframeEl);

  const container = el.closest('.media-container');
  container.replaceChildren(wrapper);
}

export default function renderIframes(els) {
  if (!iframeRenderingEnabled()) return;

  if (!els.length) return;

  els.forEach((el) => {
    if (elsProcessingMap.has(el)) {
      return;
    }

    const requestId = window.requestIdleCallback(() => {
      renderIframeEl(el);
    });

    elsProcessingMap.set(el, requestId);
  });
}
